<template>
  <div>
    <div class="d-block d-md-none" v-if="showNextBillInfo">
      <template v-if="item.campaign_product.show_next_bill_price">
        <template v-if="isSubscriptionActive(item)">
          <div class="item-img-top-text" v-if="item.next_bill_date">
            Next {{item.is_shippable ? 'ship' : 'bill'}} date : {{getShortHumanReadableUTCDate(item.next_bill_date)}}
          </div>
          <div class="item-img-top-text" v-if="isSubscriptionPauseScheduled(item)">
            Paused until {{getShortHumanReadableUTCDate(item.pause_resume_date)}}
          </div>
        </template>
        <div class="item-img-top-text" v-else-if="isSubscriptionCancelScheduled(item)">
          Cancel on {{getShortHumanReadableUTCDate(item.cancel_date)}}
        </div>
        <div class="item-img-top-text" v-else-if="isSubscriptionComplete(item)">
          No future {{item.is_shippable ? 'shipments' : 'billing'}}
        </div>
      </template>
    </div>
    <div class="order-item-container pl-0" :class="{selected: selected, 'clickable-order-container': clickable}" @click="handleClick">
      <div class="item-image">
        <img :src="getImageUrl(item)" @error="setAltImg($event, item)" alt="" />
      </div>
      <div class="order-item-info">
        <div class="item-detail-text">
          <span class="item-name">
            {{getName(item)}}
            <span v-if="showPriceNextToName"> - {{getPrice(item) | currency(currency)}}</span>
          </span>
          <span v-if="debug"> ({{item.campaign_product.campaign_product_id}})</span>
          <div v-if="showVariants && getVariant(item)" class="item-variants">
            <div v-for="option of getVariant(item).options" :key="'variant-option-' + option.id">
              <span v-if="!option.is_deleted">{{option.name}}: {{option.value}}</span>
            </div>
            <span v-if="debug">Variant ID: {{getVariant(item).variant_id}}</span>
          </div>
          <div v-else-if="showVariants && getBundledVariants(item)" class="item-variants">
            <div v-for="bundledVariant of getBundledVariants(item)" :key="'variant-' + bundledVariant.variant.id">
              <div v-for="option of bundledVariant.variant.options" :key="'variant-option-' + option.id">
                <span v-if="!option.is_deleted">
                  <span v-if="bundledVariant.variant.options.length > 2">{{option.name}}: </span>
                  {{option.value}} x {{bundledVariant.quantity}}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div class="notranslate item-detail-text" v-if="!showPriceNextToName">
          <div :class="{'original-price': isReplaced(item)}">
            <span v-if="isDiscounted(item)" class="original-price">{{getOriginalPrice(item) | currency(currency)}}</span> {{getPrice(item) | currency(currency)}}
            <span v-if="getBundleQuantity(item) > 1">&nbsp;({{getBundleQuantity(item)}} x {{getBundleUnitPrice(item) | currency(currency)}})</span>
          </div>
        </div>
        <div class="item-detail-text" v-if="showFulfillmentStatus">
          <span class="mr-2">Shipment</span>
          <span class="badge order-status" :class="getItemFulfillmentStatusClass(item)">{{getItemFulfillmentStatus(item)}}</span>
        </div>
        <div>
          <template v-if="isRecurring(item)">
            <div class="item-detail-text">
              <span class="mr-2">Subscription</span>
              <span class="badge order-status" :class="getItemStatusClass(item)">{{getItemStatus(item)}}</span>
            </div>
            <template v-if="debug && item.subscription_id">
              <div class="item-detail-text">
                Subscription ID : {{item.subscription_id}}
              </div>
              <div class="item-detail-text">
                Billing Cycle : {{item.billing_cycle}}
              </div>
            </template>
            <template v-if="item.campaign_product.show_next_bill_price">
              <template v-if="isSubscriptionActive(item)">
                <div class="item-detail-text" v-if="item.next_bill_date">
                  Next {{item.is_shippable ? 'shipment' : 'billing'}} : {{item.next_cycle_payment_due | currency(item.order.currency)}} on {{getShortHumanReadableUTCDate(item.next_bill_date)}}
                </div>
                <div class="item-detail-text" v-if="isSubscriptionPauseScheduled(item)">
                  Paused until {{getShortHumanReadableUTCDate(item.pause_resume_date)}}
                </div>
              </template>
              <div class="item-detail-text" v-else-if="isSubscriptionCancelScheduled(item)">
                Cancel on {{getShortHumanReadableUTCDate(item.cancel_date)}}
              </div>
              <div class="item-detail-text" v-else-if="isSubscriptionComplete(item)">
                No future {{item.is_shippable ? 'shipments' : 'billing'}}
              </div>
            </template>
          </template>
          <template v-else>
            <div class="item-detail-text">One-time purchase</div>
          </template>
        </div>
        <div class="item-detail-text" v-if="hasPendingCharge(item)">
          Pending charge for {{item.amount_pending | currency(currency)}}
        </div>
        <div
          class="item-detail-text next-cycle-row"
          v-if="!isSubscriptionView && (item.next_cycle_product || item.next_variant)"
        >
          <div class="next-cycle-main" :class="{'has-next-product': item.next_cycle_product}">
            <span class="next-cycle-label">Next cycle:</span>
            <img
              v-if="item.next_cycle_product"
              class="next-cycle-thumb"
              :src="getNextCycleImageUrl(item)"
              @error="setNextCycleAltImg($event, item)"
              alt=""
            />
            <div class="next-cycle-detail">
              <span v-if="item.next_cycle_product" class="next-cycle-name">{{item.next_cycle_product.name}}</span>
              <div v-if="item.next_variant" class="item-variants next-cycle-variant">
                <div v-for="option of item.next_variant.options" :key="'next-variant-option-' + option.id">
                  <span v-if="!option.is_deleted">{{option.name}}: {{option.value}}</span>
                </div>
                <span v-if="debug">Variant ID: {{item.next_variant.variant_id}}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="showButtons" class="d-none d-md-block item-select-btn-container">
        <SelectItemButtons
          :session="session"
          :selected="selected"
          :item-select-label="itemSelectLabel"
          :use-intents="useIntents"
          :intents="item['actions']"
          :item="item"
          :subscription="isSubscriptionButton"
          @select="handleSelect"
          @viewSubscriptionOrders="handleViewSubscription"
        ></SelectItemButtons>
      </div>
    </div>
    <div v-if="debug" class="d-none campaign-product-id">{{item.campaign_product.campaign_product_id}}</div>
    <div v-if="!noselect && showButtons" class="d-block d-md-none p-0">
      <hr class="m-0"/>
      <div class="card-padding item-select-btn-container">
        <SelectItemButtons
          :session="session"
          :selected="selected"
          :item-select-label="itemSelectLabel"
          :use-intents="useIntents"
          :intents="item['actions']"
          :item="item"
          :subscription="isSubscriptionButton"
          @select="handleSelect"
          @viewSubscriptionOrders="handleViewSubscription"
        ></SelectItemButtons>
      </div>
    </div>
  </div>
</template>

<script>
import {mapState, mapGetters} from 'vuex'
import {
  getItemStatus, getItemStatusClass, getItemImageUrl,
  getItemFulfillmentStatus, getItemFulfillmentStatusClass, showFulfillment,
  getShortHumanReadableUTCDate, isRecurring, setFallbackImageUrl,
  getBundleUnitPrice, getBundleQuantity
} from '@/utils'
import {BillingCycleType, ItemStatus} from '@/constants'

const SelectItemButtons = () => import('./SelectItemButtons')

export default {
  name: 'OrderItemCard',
  components: {SelectItemButtons},
  props: {
    session: Object,
    item: {
      type: Object,
      required: true
    },
    currency: {
      type: String,
      default: 'USD'
    },
    selected: {
      type: Boolean,
      default: false
    },
    showButtons: {
      type: Boolean,
      default: true
    },
    noselect: {
      type: Boolean,
      default: false
    },
    itemSelectLabel: {
      type: String,
      default: 'Select'
    },
    useIntents: {
      type: Boolean,
      default: false
    },
    isSubscriptionView: {
      type: Boolean,
      default: false
    },
    isSubscriptionButton: {
      type: Boolean,
      default: false
    },
    showNextBillInfo: {
      type: Boolean,
      default: true
    },
    clickable: {
      type: Boolean,
      default: false
    },
    showQuantity: {
      type: Boolean,
      default: false
    },
    showBillingInterval: {
      type: Boolean,
      default: false
    },
    showVariants: {
      type: Boolean,
      default: true
    },
    showPriceNextToName: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapState(['debug']),
    ...mapGetters(['subscriptionStatusLabels']),
    showFulfillmentStatus() {
      return !this.isSubscriptionView && showFulfillment(this.item)
    }
  },
  methods: {
    handleClick(event) {
      if (this.clickable) {
        this.$emit('click', event, this.item)
      }
    },
    handleSelect(...args) {
      this.$emit('select', ...args)
    },
    handleViewSubscription() {
      this.$emit('viewSubscriptionOrders', this.item)
    },
    isRecurring(item) {
      return isRecurring(item)
    },
    isSubscriptionActive(item) {
      return (item.billing_cycle_type === BillingCycleType.Recurring) && item.can_cancel_subscription
    },
    isSubscriptionPauseScheduled(item) {
      return this.isSubscriptionActive(item) && (item.status !== ItemStatus.Paused) && item.pause_resume_date
    },
    isSubscriptionCancelScheduled(item) {
      return item.cancel_date
    },
    isSubscriptionComplete(item) {
      return item.status === ItemStatus.Complete
    },
    isReplaced(item) {
      return item.status === ItemStatus.Replaced
    },
    getItemStatus(item) {
      return getItemStatus(item, this.subscriptionStatusLabels, false)
    },
    getItemStatusClass(item) {
      return getItemStatusClass(item)
    },
    getItemFulfillmentStatus(item) {
      return getItemFulfillmentStatus(item)
    },
    getItemFulfillmentStatusClass(item) {
      return getItemFulfillmentStatusClass(item)
    },
    hasPendingCharge(item) {
      return parseFloat(item.amount_pending) > Number.EPSILON
    },
    getName(item) {
      return (this.isSubscriptionView && item.next_cycle_product) ? item.next_cycle_product.name : item.name
    },
    getVariant(item) {
      return (this.isSubscriptionView && item.next_variant) ? item.next_variant : item.variant
    },
    getBundledVariants(item) {
      return this.isSubscriptionView ? item.next_bundled_variants : item.bundled_variants
    },
    getOriginalPrice(item) {
      return this.isSubscriptionView ? item.next_recurring_price : item.price
    },
    getPrice(item) {
      return this.isSubscriptionView ? item.recurring_price : item.price
    },
    isDiscounted(item) {
      return this.isSubscriptionView &&
        ((parseFloat(this.getOriginalPrice(item)) - parseFloat(this.getPrice(item))) > Number.EPSILON)
    },
    getImageUrl(item) {
      return getItemImageUrl(item, 'medium', this.isSubscriptionView)
    },
    setAltImg(event, item) {
      setFallbackImageUrl(event, item, 'medium', true, this.isSubscriptionView)
    },
    getNextCycleImageUrl(item) {
      return getItemImageUrl(item, 'small', true)
    },
    setNextCycleAltImg(event, item) {
      setFallbackImageUrl(event, item, 'small', true, true)
    },
    getBundleUnitPrice(item) {
      return getBundleUnitPrice(item, this.isSubscriptionView)
    },
    getBundleQuantity(item) {
      return getBundleQuantity(item, this.isSubscriptionView)
    },
    getShortHumanReadableUTCDate(date) {
      return getShortHumanReadableUTCDate(date)
    }
  }
}
</script>

<style>
/* Using non-scoped styles to override parent container styles */
.order-item-container {
  text-align: left !important;
  display: flex !important;
  flex-direction: row !important;
  padding: 16px !important;
  align-items: center !important;
}

.order-item-info {
  text-align: left !important;
  color: #333 !important;
  padding-left: 1.071em !important;
  align-items: center !important;
  flex: 1 !important;
}

.item-detail-text {
  text-align: left !important;
}

.item-image {
  width: 150px !important;
  height: 150px !important;
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  margin-left: 1.071em !important;
  border: 1px solid #DFE0E0 !important;
}

.item-image > img {
  width: 100% !important;
  height: 100% !important;
  object-fit: contain !important;
}

/* Scaled down version for StepTitle context (Session.vue only) */
.product-container-middle .order-item-container,
.product-container-top .order-item-container {
  padding: 12px !important;
}

.product-container-middle .item-image,
.product-container-top .item-image {
  width: 100px !important;
  height: 100px !important;
  margin-left: 0.5em !important;
}

.product-container-middle .order-item-info,
.product-container-top .order-item-info {
  padding-left: 1.5em !important;
}

.product-container-middle .item-name,
.product-container-top .item-name {
  font-size: 16px !important;
  line-height: 1.4 !important;
}

.product-container-middle .item-detail-text,
.product-container-top .item-detail-text {
  font-size: 13px !important;
  min-height: auto !important;
  line-height: 1.6 !important;
  margin-bottom: 4px !important;
}

.product-container-middle .badge,
.product-container-top .badge {
  font-size: 11px !important;
  padding: 3px 8px !important;
}

.next-cycle-main {
  display: flex !important;
  align-items: flex-start !important;
}

.next-cycle-label {
  margin-right: 8px !important;
  white-space: nowrap !important;
}

.next-cycle-thumb {
  width: 32px !important;
  height: 32px !important;
  object-fit: contain !important;
  border: 1px solid #DFE0E0 !important;
  margin-right: 8px !important;
  flex-shrink: 0 !important;
  background: #fff !important;
}

.next-cycle-detail {
  display: flex !important;
  flex-direction: column !important;
}

/* When a next-cycle product is shown, give the label + name a 32px line box
   (matching the thumbnail) so label, thumbnail and name all center on the same line. */
.next-cycle-main.has-next-product .next-cycle-label,
.next-cycle-main.has-next-product .next-cycle-name {
  min-height: 32px !important;
  display: flex !important;
  align-items: center !important;
}

.next-cycle-name + .next-cycle-variant {
  margin-top: 2px !important;
}
</style>
